import type { PlayEvent, Spot, UnitId } from './types';

/**
 * Where every Pokémon is during a play, as straight-line legs between spots. Play resolution
 * decides the times; this turns them into `move` events so the client only has to interpolate
 * (CLAUDE.md rule 2). A unit rests between legs, and its legs never overlap.
 */

interface Leg {
  t0: number;
  t1: number;
  from: Spot;
  to: Spot;
}

const lerp = (a: Spot, b: Spot, f: number): Spot => [
  a[0] + (b[0] - a[0]) * f,
  a[1] + (b[1] - a[1]) * f,
];
const dist = (a: Spot, b: Spot) => Math.hypot(b[0] - a[0], b[1] - a[1]);

export class Motion {
  private readonly legs = new Map<UnitId, Leg[]>();

  constructor(private readonly start: Readonly<Record<UnitId, Spot>>) {}

  /** The unit's spot at time t: its formation spot, then along its legs. */
  posAt(unit: UnitId, t: number): Spot {
    let at = this.start[unit];
    for (const leg of this.legs.get(unit) ?? []) {
      if (t <= leg.t0) break;
      if (t >= leg.t1) {
        at = leg.to;
        continue;
      }
      return lerp(leg.from, leg.to, (t - leg.t0) / (leg.t1 - leg.t0));
    }
    return at;
  }

  /**
   * Move the unit to `to`, leaving at t0 and arriving at t1. Whatever it was doing from t0 on is
   * replaced: a leg in progress stops where the unit is at t0.
   */
  goto(unit: UnitId, to: Spot, t0: number, t1: number): void {
    const from = this.posAt(unit, t0);
    const kept: Leg[] = [];
    for (const leg of this.legs.get(unit) ?? []) {
      if (leg.t0 >= t0) break;
      kept.push(leg.t1 <= t0 ? leg : { ...leg, t1: t0, to: from });
    }
    if (dist(from, to) > 0) {
      if (t1 <= t0) throw new Error(`${unit} cannot move ${dist(from, to)} yards in no time`);
      kept.push({ t0, t1, from, to });
    }
    this.legs.set(unit, kept);
  }

  /** Run a path of waypoints between t0 and t1 at one steady speed. */
  run(unit: UnitId, path: readonly Spot[], t0: number, t1: number): void {
    let from = this.posAt(unit, t0);
    const lengths = path.map((p) => {
      const d = dist(from, p);
      from = p;
      return d;
    });
    const total = lengths.reduce((s, d) => s + d, 0);
    let t = t0;
    path.forEach((p, i) => {
      const next = total === 0 ? t1 : t + ((t1 - t0) * (lengths[i] ?? 0)) / total;
      this.goto(unit, p, t, next);
      t = next;
    });
  }

  /** Every leg as a `move` event, cut at the whistle; legs that would start after it never happen. */
  moveEvents(endT: number): PlayEvent[] {
    const out: PlayEvent[] = [];
    for (const [unit, legs] of this.legs) {
      for (const leg of legs) {
        if (leg.t0 >= endT) continue;
        const t1 = Math.min(leg.t1, endT);
        const to =
          t1 === leg.t1 ? leg.to : lerp(leg.from, leg.to, (t1 - leg.t0) / (leg.t1 - leg.t0));
        out.push({ t: leg.t0, type: 'move', unit, from: leg.from, to, duration: t1 - leg.t0 });
      }
    }
    return out;
  }
}
